// web/src/hooks/useDialogTimeouts.ts
import { useEffect, useRef } from 'react'

export interface DialogTimeouts {
  /** Runs `callback` after `ms`, replacing any timer pending under `key`. */
  schedule: (key: string, callback: () => void, ms: number) => void
  /** Cancels the timer pending under `key`, if any. */
  clear: (key: string) => void
}

/**
 * Timers that belong to a dialog's content: every one still pending is
 * cancelled when the content unmounts, and none starts after that (a request
 * left running when the dialog closed may still finish), so none can act on a
 * dialog that has closed, or on the next one opened in its place.
 *
 * A bare `setTimeout(toggle, 10000)` outlived its dialog: closed by hand and
 * opened again, the dialog was closed by the old timer in the middle of the
 * next upload, which also re-enabled the button that sends it again.
 */
export const useDialogTimeouts = (): DialogTimeouts => {
  'use memo'
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const mounted = useRef(false)

  useEffect(() => {
    mounted.current = true
    const pending = timers.current

    return () => {
      mounted.current = false
      pending.forEach((timer) => clearTimeout(timer))
      pending.clear()
    }
  }, [])

  const clear = (key: string) => {
    const timer = timers.current.get(key)
    if (timer !== undefined) {
      clearTimeout(timer)
      timers.current.delete(key)
    }
  }

  const schedule = (key: string, callback: () => void, ms: number) => {
    clear(key)
    if (!mounted.current) {
      return
    }
    timers.current.set(
      key,
      setTimeout(() => {
        timers.current.delete(key)
        callback()
      }, ms)
    )
  }

  return { schedule, clear }
}
